/* eslint-disable class-methods-use-this */
import {CSSResult, html, LitElement, nothing, unsafeCSS,} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {createRef, ref} from 'lit/directives/ref.js';
import {classMap} from 'lit/directives/class-map.js';
import styles from './zoomable-image.scss';
import {mediaQuery} from '../../scripts/lib/mediaQuery.js';
import type { MediaQuery } from '../../@types/dom.d.ts';

const typedMediaQuery = mediaQuery as unknown as MediaQuery;

@customElement('ents-zoomable-image')
export class ZoomableImage extends LitElement {
  @property({type: String, attribute: 'zoom-src'}) zoomSrc = '';

  @property({type: Boolean, attribute: 'zoom-enabled'}) zoomEnabled = false;

  @property({type: String, attribute: 'alt'}) altText = '';

  @state() scale = 1;

  @state() isAnimatingZoom = false;

  private readonly DEFAULT_MAX_ZOOM = 2.5;
  private readonly L_MAX_ZOOM = 3.5;
  private readonly MIN_ZOOM_THRESHOLD = 1.2;
  private readonly MOUSE_ZOOM_DELAY = 100;
  private readonly UPDATE_IMAGE_ON_PINCH_DELAY = 100;
  private readonly MOUSE_ZOOM_DURATION = '0.6s';
  private readonly TOUCH_ZOOM_DURATION = '0.4s';
  private readonly ZOOM_RESET_DURATION = '0.3s';
  private readonly ZOOM_RESET_SHORT_DURATION = '0.15s';
  private readonly PINCH_START_DURATION = '0.2s';
  private readonly MIN_VELOCITY = 0.05;
  private readonly MAX_VELOCITY = 3;
  private readonly DECELERATION_CONSTANT = 0.005;

  private readonly containerRef = createRef<HTMLDivElement>();
  private img: HTMLImageElement | undefined = undefined;
  private source: HTMLSourceElement | undefined = undefined;
  private lastTouchMoveTime?: number;
  private lastTouchVelocity = {x: 0, y: 0};
  private momentumAnimationFrameId?: number;
  private momentumStartTime = 0;
  private momentumDuration = 0;
  private momentumStartOrigin = {x: 0, y: 0};
  private momentumTargetOrigin = {x: 0, y: 0};
  private lastTouchMoveX?: number;
  private lastTouchMoveY?: number;
  private currentOriginX?: number;
  private currentOriginY?: number;
  private containerWidth = 0;
  private containerHeight = 0;
  private zoomOnMouseMove = true;
  private initialPinchDistance?: number;
  private initialPinchScale = 1;
  private isPinchZooming = false;
  private updateImageTimerId?: number;
  private zoomTimerId?: number;

  static get styles(): CSSResult {
    return unsafeCSS(styles);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('touchend', this.handleGlobalTouchEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.zoomTimerId);
    clearTimeout(this.updateImageTimerId);
    this.resetTransitionEndListeners();
    document.removeEventListener('touchend', this.handleGlobalTouchEnd);
  }

  render() {
    return html`
      <div
        role=${ifDefined(this.altText ? "img" : undefined)}
        alt=${ifDefined(this.altText ? this.altText : undefined)}
        aria-labelledby=${ifDefined(this.altText ? "zoomable-image-alt-text" : undefined)}
        data-test-ents-zoomable-image
        ${ref(this.containerRef)}
        class="${classMap({
          'zoomable-image': this.zoomEnabled,
          'zoomable-image--zoomed': this.isZoomedOrZooming,
        })}"
        @mousemove=${this.onMouseMove}
        @mouseleave=${this.onMouseLeave}
        @touchstart=${this.onTouchStart}
        @touchmove=${this.onTouchMove}
        @touchend=${this.onTouchEnd}
        @click=${this.onClick}
      >
        <slot aria-hidden="true" @slotchange=${this.handleSlotChange}></slot>
      </div>
      ${this.altText ? html`
          <span id="zoomable-image-alt-text" aria-hidden="true" class="sr-only">${this.altText}</span>` :
        nothing}
    `;
  }

  /*
    detects touches outside the zoomable image and resets zoom
   */
  private readonly handleGlobalTouchEnd = (e: TouchEvent) => {
    if (this.container && this.isZoomedOrZooming && !e.composedPath().includes(this.container)) {
      this.resetImageZoom();
    }
  };

  /*
    whenever the slot is updated, updates the references to the inner <img> and <source> tags
   */
  private handleSlotChange(e: Event) {
    const slotElement = e.target as HTMLSlotElement;
    const assignedElements = slotElement.assignedElements({flatten: true});
    const pictureElement = assignedElements.find(
      el => el.tagName.toLowerCase() === 'picture'
    ) as HTMLPictureElement | undefined;

    if (pictureElement) {
      this.img = pictureElement.querySelector('img') ?? undefined;
      this.source = pictureElement.querySelector('source') ?? undefined;
    } else {
      this.img = assignedElements.find(el => el.tagName.toLowerCase() === 'img') as HTMLImageElement | undefined;
      this.source = assignedElements.find(el => el.tagName.toLowerCase() === 'source') as HTMLSourceElement | undefined;
    }
  }

  private get container(): HTMLDivElement | undefined {
    return this.containerRef.value;
  }

  private get isZoomedOrZooming() {
    return this.scale !== 1 || this.isAnimatingZoom;
  }

  private get maxZoom() {
    return typedMediaQuery.L_and_up && !typedMediaQuery.XL_and_up ? this.L_MAX_ZOOM : this.DEFAULT_MAX_ZOOM;
  }

  /*
    zooms the image to maxZoom on click/tap
   */
  private zoomImage(e: Event) {
    if (!this.zoomEnabled || !this.container) {
      return;
    }

    if (this.isZoomedOrZooming) {
      // the image is already zoomed, reset the zoom instead
      this.resetImageZoom();
      return;
    }

    this.resetTransitionEndListeners();
    const {x, y} = this.extractEventCoordinates(e);
    this.isAnimatingZoom = true;
    this.container.style.transition = `transform ${this.isMouseEvent(e) ? this.MOUSE_ZOOM_DURATION : this.TOUCH_ZOOM_DURATION} ease-out`;
    this.container.style.transform = `scale(${this.maxZoom})`;
    this.container.style.transformOrigin = `${x}px ${y}px`;
    this.currentOriginX = x;
    this.currentOriginY = y;
    this.container.addEventListener('transitionend', this.handleZoomTransitionEnd, {once: true});
  }

  private readonly handleZoomTransitionEnd = () => {
    this.scale = this.maxZoom;
    this.updateImageSource();
    this.isAnimatingZoom = false;
  };

  /*
    calculates and applies the new zoom scale during pinch to zoom gesture
   */
  private updateZoomScale(e: TouchEvent) {
    if (!this.zoomEnabled || !this.container || !this.initialPinchDistance) {
      return;
    }

    this.isPinchZooming = true;
    clearTimeout(this.updateImageTimerId);
    e.preventDefault();
    e.stopPropagation();

    const currentDistance = this.getDistanceBetweenTouches(e.touches[0], e.touches[1]);
    const scaleFactor = currentDistance / this.initialPinchDistance;
    let newScale = this.initialPinchScale * scaleFactor;
    newScale = Math.max(1, Math.min(newScale, this.maxZoom));
    this.scale = newScale;
    this.container.style.transform = `scale(${newScale})`;
    this.updateImageTimerId = window.setTimeout(() => this.updateImageSource(), this.UPDATE_IMAGE_ON_PINCH_DELAY);
  }

  /*
    shifts the image so that the zoom center follows the mouse/finger
   */
  private moveZoomCenter(e: Event) {
    if (!this.zoomEnabled || !this.container || !this.isZoomedOrZooming) {
      return;
    }

    this.isPinchZooming = false;
    e.preventDefault();
    e.stopPropagation();
    let {x, y} = this.extractEventCoordinates(e);

    if (!this.isMouseEvent(e)) {
      // touch events require some transformation of the coordinates to account for the current scale
      // as well as velocity calculations in order to apply "momentum" to the movement

      const prevX = this.lastTouchMoveX ?? x;
      const prevY = this.lastTouchMoveY ?? y;
      const now = performance.now();
      const dt = this.lastTouchMoveTime ? now - this.lastTouchMoveTime : 16; // fallback ~16ms

      const deltaX = x - prevX;
      const deltaY = y - prevY;

      this.lastTouchVelocity = {
        x: deltaX / dt,
        y: deltaY / dt,
      };

      this.lastTouchMoveTime = now;
      this.lastTouchMoveX = x;
      this.lastTouchMoveY = y;

      x = Math.max(
        0,
        Math.min((this.currentOriginX ?? 0) - (deltaX / (this.scale - 1 || 1)), this.containerWidth)
      );
      y = Math.max(
        0,
        Math.min((this.currentOriginY ?? 0) - (deltaY / (this.scale - 1 || 1)), this.containerHeight)
      );
    }

    if (!this.isAnimatingZoom) {
      // disable animation when shifting image
      this.container.style.transition = '';
    }

    this.container.style.transformOrigin = `${x}px ${y}px`;
    this.currentOriginX = x;
    this.currentOriginY = y;
  }

  /*
    adds momentum to image shift (mobile only)
   */
  private applyMomentum() {
    // only apply momentum if zoom is active, not pinching, and movement velocity is above MIN_VELOCITY
    if (
      this.scale === 1 ||
      this.isPinchZooming ||
      (Math.abs(this.lastTouchVelocity.x) < this.MIN_VELOCITY && Math.abs(this.lastTouchVelocity.y) < this.MIN_VELOCITY)
    ) {
      return;
    }

    // limit velocity to MAX_VELOCITY
    const velocityX = Math.max(-this.MAX_VELOCITY, Math.min(this.lastTouchVelocity.x, this.MAX_VELOCITY));
    const velocityY = Math.max(-this.MAX_VELOCITY, Math.min(this.lastTouchVelocity.y, this.MAX_VELOCITY));

    // calculate momentum distances for x and y using: d = v² / (2 * deceleration)
    const momentumDistanceX = (velocityX ** 2) / (2 * this.DECELERATION_CONSTANT) * (velocityX < 0 ? -1 : 1);
    const momentumDistanceY = (velocityY ** 2) / (2 * this.DECELERATION_CONSTANT) * (velocityY < 0 ? -1 : 1);

    // calculate duration for the momentum effect (in ms)
    const durationX = Math.abs(velocityX) / this.DECELERATION_CONSTANT;
    const durationY = Math.abs(velocityY) / this.DECELERATION_CONSTANT;
    const momentumDuration = Math.max(durationX, durationY);

    // convert the momentum into a shift in transform origin.
    const scaleFactor = this.scale - 1 || 1;
    const targetOriginX = Math.round((this.currentOriginX ?? 0) - momentumDistanceX / scaleFactor);
    const targetOriginY = Math.round((this.currentOriginY ?? 0) - momentumDistanceY / scaleFactor);

    // save the start and target values for the animation
    this.momentumStartTime = performance.now();
    this.momentumDuration = momentumDuration;
    this.momentumStartOrigin = {x: this.currentOriginX ?? 0, y: this.currentOriginY ?? 0};
    this.momentumTargetOrigin = {x: targetOriginX, y: targetOriginY};

    // start the animation
    this.animateMomentum();

    // reset velocity tracking
    this.lastTouchVelocity = {x: 0, y: 0};
    this.lastTouchMoveTime = undefined;
  }

  /*
    animates momentum on image shift (mobile only)
   */
  private animateMomentum() {
    const now = performance.now();
    const elapsed = now - this.momentumStartTime;
    const t = Math.min(elapsed / this.momentumDuration, 1);

    // ease-out quadratic function for a smooth deceleration
    const easeOut = 1 - Math.pow(1 - t, 2);

    let currentX = this.momentumStartOrigin.x +
      (this.momentumTargetOrigin.x - this.momentumStartOrigin.x) * easeOut;
    let currentY = this.momentumStartOrigin.y +
      (this.momentumTargetOrigin.y - this.momentumStartOrigin.y) * easeOut;

    // clamp the new origin within the container boundaries
    currentX = Math.max(0, Math.min(currentX, this.containerWidth));
    currentY = Math.max(0, Math.min(currentY, this.containerHeight));

    if (this.container) {
      // update transform-origin with the current values
      this.container.style.transformOrigin = `${currentX}px ${currentY}px`;
      this.currentOriginX = currentX;
      this.currentOriginY = currentY;
    }

    if (t < 1 && ((currentX > 0 && currentX < this.containerWidth) || (currentY > 0 && currentY < this.containerHeight))) {
      this.momentumAnimationFrameId = requestAnimationFrame(() => this.animateMomentum());
    } else {
      this.momentumAnimationFrameId = undefined; // animation complete
    }
  }

  /*
    cancels momentum applied to image shift (mobile only)
   */
  private cancelMomentum() {
    if (this.momentumAnimationFrameId) {
      cancelAnimationFrame(this.momentumAnimationFrameId);
      this.momentumAnimationFrameId = undefined;
    }
  }

  /*
    resets zoom to intial state (scale = 1)
   */
  private resetImageZoom() {
    if (this.container) {
      this.resetTransitionEndListeners();
      this.isAnimatingZoom = true;
      const duration = this.scale < this.MIN_ZOOM_THRESHOLD ? this.ZOOM_RESET_SHORT_DURATION : this.ZOOM_RESET_DURATION;
      this.container.style.transition = `transform ${duration} ease-out`;
      this.container.style.transform = '';
      this.container.addEventListener('transitionend', this.handleResetZoomTransitionEnd, {once: true});
    }
  }

  private readonly handleResetZoomTransitionEnd = () => {
    this.resetStyles();
    this.isAnimatingZoom = false;
  };

  private resetTransitionEndListeners() {
    this.container?.removeEventListener('transitionend', this.handleZoomTransitionEnd);
    this.container?.removeEventListener('transitionend', this.handleResetZoomTransitionEnd);
  }

  /*
    sets initial values and zoom center before a pinch to zoom gesture begins
   */
  private preparePinchToZoom(e: TouchEvent, rect: DOMRect) {
    if (!this.zoomEnabled || !this.container) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    this.initialPinchDistance = this.getDistanceBetweenTouches(e.touches[0], e.touches[1]);
    this.initialPinchScale = this.scale;

    let {x, y} = this.extractEventCoordinates(e);
    x = Math.max(0, Math.min((x - rect.left) / this.scale, this.containerWidth));
    y = Math.max(0, Math.min((y - rect.top) / this.scale, this.containerHeight));
    this.container.style.transition = `transform-origin ${this.PINCH_START_DURATION} ease-out`;
    this.container.style.transformOrigin = `${x}px ${y}px`;
    this.currentOriginX = x;
    this.currentOriginY = y;
  }

  /*
    sets will-change so browsers can optimize zoom animations
   */
  private optimizeZoomAnimations() {
    if (this.zoomEnabled && this.container) {
      this.container.style.willChange = 'transform, transform-origin';
    }
  }

  /*
    resets styling used by zoom functions
   */
  private resetStyles() {
    if (this.container) {
      this.container.style.transition = '';
      this.container.style.transform = '';
      this.container.style.transformOrigin = '';
      this.container.style.willChange = '';
      this.currentOriginX = 0;
      this.currentOriginY = 0;
      this.scale = 1;
    }
  }

  /*
    replaces image source with zoom source if zoom scale is above threshold
   */
  private updateImageSource() {
    if (this.scale >= this.MIN_ZOOM_THRESHOLD && this.img && this.img.src !== this.zoomSrc) {
      this.img.src = this.zoomSrc;
      this.source?.removeAttribute('srcset');
    }
  }

  private getDistanceBetweenTouches(t1: Touch, t2: Touch): number {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /*
    returns the x,y coordinates of different kinds of events (mouse, touch, pointer)
   */
  private extractEventCoordinates(e: Event): { x: number; y: number } {
    if (this.isMouseEvent(e)) {
      const me = e as MouseEvent;
      if (me.offsetX === 0 && me.offsetY === 0 && me.layerX >= 0 && me.layerY >= 0) {
        // for firefox, which doesn't support offsetX/Y properly
        return {x: me.layerX, y: me.layerY};
      }
      return {x: me.offsetX, y: me.offsetY};
    } else if ('touches' in e && (e as TouchEvent).touches.length) {
      const touches = (e as TouchEvent).touches;
      if (touches.length === 2) {
        // for 2 fingers, return the coordinates of the center between the fingers
        const x = (touches[0].clientX + touches[1].clientX) / 2;
        const y = (touches[0].clientY + touches[1].clientY) / 2;
        return {x, y};
      } else {
        return {x: touches[0].clientX, y: touches[0].clientY};
      }
    } else if ('pointerType' in e) {
      const pe = e as PointerEvent;
      return {x: pe.offsetX, y: pe.offsetY};
    }
    return {x: 0, y: 0};
  }

  private isMouseEvent(e: Event): boolean {
    if (e instanceof PointerEvent) {
      return e.pointerType === 'mouse';
    }
    return e instanceof MouseEvent;
  }

  // LISTENERS

  private onMouseMove(e: MouseEvent) {
    if (!this.zoomEnabled) {
      return;
    }

    if (e.movementX === 0 && e.movementY === 0) {
      // no need to process mouse move events with no movement
      return;
    }

    if (this.zoomOnMouseMove && !this.isZoomedOrZooming) {
      // trigger zoom after a timeout while mouse is moving over unzoomed image (unless the image has been clicked -> zoomOnMouseMove is false)
      clearTimeout(this.zoomTimerId);
      this.optimizeZoomAnimations();
      this.zoomTimerId = window.setTimeout(() => this.zoomImage(e), this.MOUSE_ZOOM_DELAY);
    } else {
      // shift the zoomed image to follow the mouse
      this.moveZoomCenter(e);
    }
  }

  private onMouseLeave() {
    if (!this.zoomEnabled) {
      return;
    }

    this.zoomOnMouseMove = true; // re-enable zooming on mouse move
    clearTimeout(this.zoomTimerId); // cancel zoom timer to prevent image from zooming once mouse has left
    if (this.isZoomedOrZooming) {
      // reset image if it is zoomed
      this.resetImageZoom();
    } else {
      this.resetStyles();
    }
  }

  private onClick(e: Event) {
    if (!this.zoomEnabled || this.isAnimatingZoom) {
      return; // ignore clicks if a zoom animation is already in progress
    }
    this.optimizeZoomAnimations();
    this.zoomOnMouseMove = false; // disable zooming on mouse move until the mouse leaves the image
    clearTimeout(this.zoomTimerId); // cancel scheduled image zoom
    this.zoomImage(e); // zoom (or unzoom) image
  }

  private onTouchStart(e: TouchEvent) {
    if (!this.zoomEnabled) {
      return;
    }

    this.cancelMomentum(); // cancel any momentum applied to an image shift due to new touch

    // reset touch move coordinates due to new touch
    this.lastTouchMoveX = undefined;
    this.lastTouchMoveY = undefined;

    if (this.container) {
      this.optimizeZoomAnimations();

      const rect = this.container.getBoundingClientRect();

      if (!this.isZoomedOrZooming) {
        // update container dimensions for unzoomed state
        this.containerWidth = rect.width;
        this.containerHeight = rect.height;
      }

      if (e.touches.length === 2 && !this.isAnimatingZoom) {
        // we are not animating zoom from a tap and 2 fingers are touching the image, prepare for pinch
        this.preparePinchToZoom(e, rect);
      }
    }
  }

  private onTouchMove(e: TouchEvent) {
    if (!this.zoomEnabled) {
      return;
    }

    if (e.touches.length === 2 && !this.isAnimatingZoom) {
      this.updateZoomScale(e); // we are not animating zoom from a tap and 2 fingers are moving over the image: update zoom level
    } else {
      this.moveZoomCenter(e); // single finger is moving over the image: shift the image to follow the finger
    }
  }

  private onTouchEnd(e: TouchEvent) {
    if (!this.zoomEnabled) {
      return;
    }

    if (e.touches.length === 0) {
      this.applyMomentum(); // add momentum to the previous image shift
      if (this.scale !== 1 && this.scale < this.MIN_ZOOM_THRESHOLD) {
        this.resetImageZoom(); // reset image zoom if the zoom scale is too small
      } else if (!this.isZoomedOrZooming) {
        this.resetStyles();
      }
      this.isPinchZooming = false; // if a pinch was happening, it has now ended
    }
  }
}
